import { useState } from 'react';

const DRAFT_PURCHASE_ORDER_SEARCH_PREFIX = 'draft:';
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function draftPurchaseOrderRouteSearch(draftPurchaseOrderId: string) {
  return {
    type: 'po' as const,
    search: `${DRAFT_PURCHASE_ORDER_SEARCH_PREFIX}${draftPurchaseOrderId}`,
  };
}

export function draftPurchaseOrderDetailTarget(search: string | undefined) {
  if (!search?.startsWith(DRAFT_PURCHASE_ORDER_SEARCH_PREFIX)) return null;
  const id = search.slice(DRAFT_PURCHASE_ORDER_SEARCH_PREFIX.length);
  return UUID_PATTERN.test(id) ? { id, trackingNo: null } : null;
}

export function clearDraftPurchaseOrderRouteSelection(
  search: string | undefined,
  clear: () => void,
) {
  if (draftPurchaseOrderDetailTarget(search)) clear();
}

export type DraftPurchaseOrderDetailTarget = {
  id: string;
  trackingNo: string | null;
};

/**
 * The draft PO whose detail slide-in is open, following the route's `draft:`
 * search. A link to another draft while the page is already open (a note
 * mention in the bell) changes only the search, so the selection re-derives
 * from it instead of keeping the first one. A route-opened order closes when
 * its `draft:` search goes away (browser Back); a row-clicked one stays.
 */
export function useDraftPurchaseOrderDetailTarget(search: string | undefined) {
  const [selection, setSelection] = useState<{
    target: DraftPurchaseOrderDetailTarget | null;
    fromRoute: boolean;
  }>(() => ({
    target: draftPurchaseOrderDetailTarget(search),
    fromRoute: true,
  }));
  const [seenSearch, setSeenSearch] = useState(search);
  if (search !== seenSearch) {
    setSeenSearch(search);
    const routed = draftPurchaseOrderDetailTarget(search);
    if (routed && routed.id !== selection.target?.id) {
      setSelection({ target: routed, fromRoute: true });
    } else if (!routed && selection.fromRoute && selection.target) {
      setSelection({ target: null, fromRoute: false });
    }
  }
  const setTarget = (target: DraftPurchaseOrderDetailTarget | null) =>
    setSelection({ target, fromRoute: false });
  return [selection.target, setTarget] as const;
}
